import { Box, Flex, Text } from '@chakra-ui/react';
import { format, isValid, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { Input } from '@revelio/ui';

import type { FilterOperator } from '../../data-builder.store';
import styles from './date-filter.module.css';
import { customFilterInputCssVars } from './disabled-input-overrides';
import { RequiredHiddenInput } from './RequiredHiddenInput';
import { isInBetweenOperator } from './string-filter-operators';

type DateFilterValueInputProps = {
  values: string[];
  onChange: (values: string[]) => void;
  operator: FilterOperator | null;
  isDisabled: boolean;
  required?: boolean;
};

function parseDateValue(value: string | undefined): Date | null {
  if (value === undefined || value === '') {
    return null;
  }

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

function formatDateValue(value: Date | null): string {
  if (value === null) {
    return '';
  }

  return format(value, 'yyyy-MM-dd');
}

type SingleDateInputProps = {
  value: string | undefined;
  onChange: (value: string) => void;
  isDisabled: boolean;
  required: boolean;
  testId: string;
  placeholder: string;
  minDate?: Date | null;
  maxDate?: Date | null;
};

function SingleDateInput({
  value,
  onChange,
  isDisabled,
  required,
  testId,
  placeholder,
  minDate,
  maxDate,
}: SingleDateInputProps) {
  return (
    <div className={styles.datePicker}>
      <DatePicker
        selected={parseDateValue(value)}
        onChange={(date) => {
          onChange(formatDateValue(date));
        }}
        dateFormat="yyyy-MM-dd"
        disabled={isDisabled}
        minDate={minDate ?? undefined}
        maxDate={maxDate ?? undefined}
        popperPlacement="bottom-end"
        popperProps={{ strategy: 'fixed' }}
        customInput={
          <Input
            size="sm"
            placeholder={placeholder}
            required={required}
            cssVars={customFilterInputCssVars}
            data-testid={testId}
          />
        }
      />
    </div>
  );
}

export function DateFilterValueInput({
  values,
  onChange,
  operator,
  isDisabled,
  required = false,
}: DateFilterValueInputProps) {
  const isBetweenOperator = isInBetweenOperator(operator);
  const hasRequiredValues = isBetweenOperator
    ? values[0] !== undefined &&
      values[0] !== '' &&
      values[1] !== undefined &&
      values[1] !== ''
    : values[0] !== undefined && values[0] !== '';

  if (isBetweenOperator) {
    const startDate = parseDateValue(values[0]);
    const endDate = parseDateValue(values[1]);
    const hasStartValue = values[0] !== undefined && values[0] !== '';
    const hasEndValue = values[1] !== undefined && values[1] !== '';

    return (
      <Flex gap={2} alignItems="center" width="100%">
        <Box position="relative" width="100%">
          <RequiredHiddenInput
            isRequired={required && !isDisabled}
            hasValue={hasStartValue}
          />
          <Box width="100%">
            <SingleDateInput
              value={values[0]}
              onChange={(value) => {
                onChange([value, values[1] ?? '']);
              }}
              isDisabled={isDisabled}
              required={required}
              testId="date-filter-start-input"
              placeholder="Start date"
              maxDate={endDate}
            />
          </Box>
        </Box>
        <Text fontSize="sm" whiteSpace="nowrap">
          to
        </Text>
        <Box position="relative" width="100%">
          <RequiredHiddenInput
            isRequired={required && !isDisabled}
            hasValue={hasEndValue}
          />
          <Box width="100%">
            <SingleDateInput
              value={values[1]}
              onChange={(value) => {
                onChange([values[0] ?? '', value]);
              }}
              isDisabled={isDisabled}
              required={required}
              testId="date-filter-end-input"
              placeholder="End date"
              minDate={startDate}
            />
          </Box>
        </Box>
      </Flex>
    );
  }

  return (
    <Box position="relative">
      <RequiredHiddenInput
        isRequired={required && !isDisabled}
        hasValue={hasRequiredValues}
      />
      <SingleDateInput
        value={values[0]}
        onChange={(value) => {
          onChange([value]);
        }}
        isDisabled={isDisabled}
        required={required}
        testId="date-filter-value-input"
        placeholder="Select date"
      />
    </Box>
  );
}
